import {
    createStyles,
    Dialog,
    Grid,
    makeStyles,
    Theme,
    Typography
} from "@material-ui/core"
import React, { memo, useCallback, useMemo } from "react"
import { useSelector } from "react-redux"
import ErrorHandlerModal from "shared/Modules/Error/Components/ErrorHandlerModal";
import { Localized } from '@fluent/react';
import { DateTimeFactory, useDateTime } from "shared/Modules/Localization/useDateTime"
import Screen from 'shared/Components/Skeleton/Screen'
import { faCalendarCheck, faSignOut } from "@fortawesome/pro-solid-svg-icons"
import clsx from "clsx"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
    IMeetingPlanningOrderCompleteModal,
    IMeetingPlanningOrderModalAction
} from "../../shared/Types/MeetingPlanning/deliveryForm"
import { selectResourceBookingModule } from "shared/Modules/Properties/propertySelectors"

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            backgroundColor: theme.palette.common.white,
        },
        wrap: {
            padding: '65px 0',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '520px'
        },
        mr_2: {
            marginRight: theme.spacing(2),
        },
        mb_1: {
            marginBottom: theme.spacing(1),
        },
        mb_2: {
            marginBottom: theme.spacing(2),
        },
        mb_4: {
            marginBottom: theme.spacing(4),
        },
        bookingTitle: {
            fontSize: '24px',
            fontWeight: 700
        },
        bookingTimeTitle: {
            color: 'rgba(0, 0, 0, 0.6)',
        },
        bookingTimeValue: {
            color: '#505050',
            fontWeight: 600,
            marginLeft: theme.spacing(1)
        },
        choice: {
            marginTop: '96px',
            marginBottom: '16px'
        },
        choiceTitle: {
            fontSize: '18px',
        },
        nextChoicesButton: {
            display: 'flex',
            alignItems: 'center',
            padding: '12px',
            marginBottom: '12px',
            border: '1px solid #E2E8F0',
            borderRadius: '12px',
            background: 'unset',
            width: '100%'
        },
        nextChoicesButtonText: {
            fontWeight: 500,
        },
        nextChoicesCalendarCheckButtonIcon: {
            width: "52px",
            height: "52px",
            borderRadius: '8px',
            backgroundColor: "#F3E8FF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
        },
        nextChoicesSignOutButtonIcon: {
            width: "52px",
            height: "52px",
            borderRadius: '8px',
            backgroundColor: "#FEE2E2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
        },
    }),
);

interface IModalProps {
    meetingPlanningCompleteModalData: IMeetingPlanningOrderCompleteModal
    onClose: (actionType: IMeetingPlanningOrderModalAction) => void
}

interface IModalContentProps {
    classes: ReturnType<typeof useStyles>
    isEdit: boolean
    isResourceBookingOrder: boolean
    showResourceBookingModule: boolean
    dateTimeFactory: DateTimeFactory
    deliveryDate: string
    deliveryTime: string
    onAccept: () => void
    onClose: () => void
}

const ModalContent = ({
   classes,
   isEdit,
   isResourceBookingOrder,
   showResourceBookingModule,
   dateTimeFactory,
   deliveryDate,
   deliveryTime,
   onAccept,
   onClose
}: IModalContentProps) => {

    const title = useMemo(() => {
        if (isResourceBookingOrder) {
            return {
                id: 'meeting-planned-added',
                text: 'Mødeforplejning tilføjet'
            }
        }

        if (isEdit) {
            return {
                id: 'meeting-planned-updated',
                text: 'Mødeservice opdateret'
            }
        }

        return {
            id: 'meeting-planned-complete',
            text: 'Ordren er gennemført'
        }
    }, [isEdit, isResourceBookingOrder])

    return <Screen
        name="statusView"
        fitPage
        fullHeight
        showPrimaryTopBar
        onBackButtonPress={onClose}
        backButtonIcon="cross"
    >
        <Grid container className={classes.wrap} direction="column" wrap="nowrap">
            <Grid container item alignItems="center" justifyContent="center" className={classes.mb_4}>
                <Localized id={title.id}>
                    <Typography variant="h4" className={classes.bookingTitle}>{title.text}</Typography>
                </Localized>
            </Grid>
            <Grid container item alignItems="center" justifyContent="center">
                <Localized id="meetingBooked-when">
                    <Typography variant="body1" className={classes.bookingTimeTitle}>Hvornår:</Typography>
                </Localized>
                <Typography variant="body1" className={classes.bookingTimeValue}>
                    {dateTimeFactory.formatDate(
                        dateTimeFactory.fromISO(`${deliveryDate}T${deliveryTime}`)
                    )}
                    {' '}
                    {dateTimeFactory.formatTime(dateTimeFactory.fromISO(`${deliveryDate}T${deliveryTime}`))}
                </Typography>
            </Grid>
            <Grid container item alignItems="center" justifyContent="center" className={classes.choice}>
                <Localized id="meetingBooked-next-choice">
                    <Typography variant="h5" className={classes.choiceTitle}>Hvad vil du foretage dig nu?</Typography>
                </Localized>
            </Grid>
            <Grid container item alignItems="center" justifyContent="center">
                {isEdit ? null : (showResourceBookingModule && <button className={classes.nextChoicesButton} onClick={onAccept}>
                        <span className={clsx(classes.nextChoicesCalendarCheckButtonIcon, classes.mr_2)}>
                            <FontAwesomeIcon icon={faCalendarCheck} size="lg" color="#A855F7" />
                        </span>
                    <Localized id="meetingBooked-next-choice-go-to-resource-booking">
                        <Typography variant="body1" className={classes.nextChoicesButtonText}>Tilføj ressourcebooking til ordre</Typography>
                    </Localized>
                </button>)}
                <button className={classes.nextChoicesButton} onClick={onClose}>
                        <span className={clsx(classes.nextChoicesSignOutButtonIcon, classes.mr_2)}>
                            <FontAwesomeIcon icon={faSignOut} size="lg" color="#EF4444" />
                        </span>
                    <Localized id="meetingBooked-next-choice-finish">
                        <Typography variant="body1" className={classes.nextChoicesButtonText}>Jeg er færdig</Typography>
                    </Localized>
                </button>
            </Grid>
        </Grid>
    </Screen>
}

const Modal = ({ meetingPlanningCompleteModalData, onClose }: IModalProps) => {
    const { isOpen, isEdit, isResourceBookingOrder, deliveryDate, deliveryTime } = meetingPlanningCompleteModalData
    const showResourceBookingModule = useSelector(selectResourceBookingModule)

    const classes = useStyles()
    const dateTimeFactory = useDateTime()

    const handleAccept = useCallback(() => onClose(IMeetingPlanningOrderModalAction.Accept), [onClose])

    const handleClose = useCallback(() => {
        if (isEdit) {
            return onClose(IMeetingPlanningOrderModalAction.Edit)
        }

        onClose(IMeetingPlanningOrderModalAction.Close)
    }, [onClose, isEdit])

    return (
        <div>
            <Dialog
                fullScreen
                open={isOpen}
            >
                <ErrorHandlerModal close={handleClose}>
                    <ModalContent
                        classes={classes}
                        isEdit={isEdit}
                        isResourceBookingOrder={isResourceBookingOrder}
                        showResourceBookingModule={showResourceBookingModule}
                        dateTimeFactory={dateTimeFactory}
                        deliveryDate={deliveryDate}
                        deliveryTime={deliveryTime}
                        onAccept={handleAccept}
                        onClose={handleClose}
                    />
                </ErrorHandlerModal>
            </Dialog>
        </div>
    );
}

export default memo(Modal);
